// src/pages/VetosView.js
import React, { useState, useEffect } from "react";
import { Card, Button, Form, Table } from "react-bootstrap";
import api from "../api.js";
import LoadingIndicator from "../components/LoadingIndicator";
import EditTabModal from "./EditTabModal";
import TabKilla from "../components/TabKilla";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

// Placeholders for the typewriter effect
const PLACEHOLDERS = [
  "picking a new hobby",
  "choosing a conference to attend",
  "selecting Q3 goals",
  "narrowing down vacation spots",
  "picking a book to read next",
  "choosing tonight’s dinner recipe",
  "choosing between different offers",
  "narrowing down options",
  "selecting which side hustle to focus on",
  "deciding which friend to invite",
  "choosing which course to enroll in",
  "picking a weekend activity",
];

// Namespaced key for localStorage
const getNamespacedKey = key => {
  const uid = localStorage.getItem("user_id");
  return uid ? `${key}_${uid}` : key;
};
const STORAGE_KEY = getNamespacedKey("vetosState");

export default function VetosView({ onStepChange }) {
  const token = localStorage.getItem("token");
  // Load persisted state
  const loadState = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };
  const saved = loadState();

  // Loading overlay and modal state
  const [loading, setLoading] = useState(false);
  const [loadingTab, setLoadingTab] = useState(false);
  const [showTabModal, setShowTabModal] = useState(false);
  const [tabData, setTabData] = useState(null);
  const [showTabKilla, setShowTabKilla] = useState(false);

  // Persisted form & flow state
  const [step, setStep]           = useState(saved.step           ?? 0);
  const [objective, setObjective] = useState(saved.objective     ?? "");
  const [options, setOptions]     = useState(saved.options       ?? [{ name:"", desc:"" }]);
  const [numToPick, setNumToPick] = useState(saved.numToPick     ?? 1);

  // After /questions call
  const [questions, setQuestions] = useState(saved.questions     ?? []);
  // Record each answer item
  const [answers, setAnswers]     = useState(saved.answers       ?? []);
  const [qIndex, setQIndex]       = useState(saved.qIndex        ?? 0);

  // For display after finalize
  const [result, setResult]       = useState(saved.result        ?? { options: options.map(o=>o.name), num_to_pick: numToPick });

  // Controls whether the Continue button (after results) is shown
  const [readyToContinue, setReadyToContinue] = useState(false);

  // Typewriter placeholder (not persisted)
  const [phIndex, setPhIndex]     = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [placeholder, setPlaceholder] = useState("");

  // --- Notify parent of step changes ---
  useEffect(() => {
    if (onStepChange) onStepChange(step);
  }, [step, onStepChange]);
  // --------------------------------------

  // Add / remove option rows
  const addRow    = () => setOptions(o => [...o, { name:"", desc:"" }]);
  const removeRow = i => setOptions(o => o.filter((_, idx) => idx !== i));

  // 1) Submit to /vetos/questions
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(
        `${API_BASE_URL}/vetos/questions`,
        { objective, options, num_to_pick: numToPick },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuestions(data.questions);
      setAnswers([]);
      setQIndex(0);
      setStep(2);
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 2) Record an answer and (if last) finalize
  const handleAnswer = async branchKey => {
    const q = questions[qIndex];
    const pts = q[branchKey]?.points_awarded || [];
    const nextAnswers = [
      ...answers,
      { question: q.question, branch: branchKey, points_awarded: pts }
    ];
    setAnswers(nextAnswers);

    if (qIndex + 1 < questions.length) {
      setQIndex(qIndex + 1);
    } else {
      // last question → auto‐finalize
      setLoading(true);
      try {
        const payload = {
          questions,
          answers: nextAnswers,
          options: options.map(o => o.name),
          num_to_pick: numToPick
        };
        const { data } = await api.post(
          `${API_BASE_URL}/vetos/finalize`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setResult(prev => ({ ...prev, ...data }));
        setStep(4);
        setReadyToContinue(true); // Allow Continue after results loaded
      } catch (err) {
        alert("Error: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
  };

  // Persist state when relevant pieces change
  useEffect(() => {
    const st = { step, objective, options, numToPick, questions, answers, qIndex, result };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(st));
  }, [step, objective, options, numToPick, questions, answers, qIndex, result]);

  // Typewriter cycling effect
  useEffect(() => {
    const word = PLACEHOLDERS[phIndex];
    if (charIndex < word.length) {
      const t = setTimeout(() => {
        setPlaceholder(word.slice(0, charIndex + 1));
        setCharIndex(ci => ci + 1);
      }, 60);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      setCharIndex(0);
      setPhIndex(pi => (pi + 1) % PLACEHOLDERS.length);
    }, 1800);
    return () => clearTimeout(t);
  }, [phIndex, charIndex]);

  // Show "Continue" after results are loaded (step 4)
  useEffect(() => {
    if (step === 4) {
      setReadyToContinue(true);
    } else {
      setReadyToContinue(false);
    }
  }, [step]);

  // On Continue: trigger modal
  const handleContinue = async () => {
    setLoadingTab(true);
    try {
      const input_content = {
        objective,
        options,
        num_to_pick: numToPick
      };
      const onboarding = JSON.parse(localStorage.getItem("onboardingAnswers") || "[]");
      const resp = await fetch(
        `${API_BASE_URL}/tabs/generate-tab/vetos`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            input_content,
            onboarding_answers: onboarding,
          }),
        }
      );
      const data = await resp.json();
      if (resp.ok && data) {
        const generatedTab = {
          custom_label: data.custom_label || "",
          domain: Array.isArray(data.domain) ? data.domain[0] || "" : (data.domain || ""),
          intent: Array.isArray(data.intent) ? data.intent[0] || "" : (data.intent || ""),
          barrier: Array.isArray(data.barrier) ? data.barrier : [],
          stage: Array.isArray(data.stage) ? data.stage : [],
          relational_context: Array.isArray(data.relational_context) ? data.relational_context : [],
          emotions: Array.isArray(data.emotions) ? data.emotions : [],
        };
        setTabData(generatedTab);
        setShowTabModal(true);
      } else {
        alert("Failed to generate tab.");
      }
    } catch (err) {
      alert("Error generating tab: " + (err.message || err));
    }
    setLoadingTab(false);
  };

  // After modal/TabKilla, fully reset for new round
  const handleReset = () => {
    setStep(0);
    setObjective("");
    setOptions([{ name: "", desc: "" }]);
    setNumToPick(1);
    setQuestions([]);
    setAnswers([]);
    setQIndex(0);
    setResult({ options: [], num_to_pick: 1 });
    setShowTabModal(false);
    setTabData(null);
    setShowTabKilla(false);
    setReadyToContinue(false);
  };

  return (
    <>
      {loading && (
        <div style={{
          position: "fixed", top:0,left:0,
          width:"100vw",height:"100vh",
          background:"rgba(255,255,255,0.7)",
          display:"flex",alignItems:"center",justifyContent:"center",
          zIndex:9999
        }}>
          <LoadingIndicator />
        </div>
      )}
      {loadingTab && (
        <div style={{
          position: "fixed", top: 0, left: 0,
          width: "100vw", height: "100vh",
          background: "rgba(255,255,255,0.7)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 99999
        }}>
          <LoadingIndicator />
        </div>
      )}
      <EditTabModal
        open={showTabModal}
        tabData={tabData}
        onClose={() => setShowTabModal(false)}
        onTabSave={(_, closed) => {
          setShowTabModal(false);
          if (closed) {
            setShowTabKilla(true);
            setTimeout(() => {
              setShowTabKilla(false);
              handleReset();
            }, 1600);
          } else {
            handleReset();
          }
        }}
      />
      {showTabKilla && <TabKilla />}

      <Card className="p-3">
        {step === 0 && (
          <>
            <h4>What’s your objective?</h4>
            <Form.Control
              value={objective}
              onChange={e => setObjective(e.target.value)}
              placeholder={`e.g. ${placeholder}`}
            />
            <Button
              onClick={() => setStep(1)}
              className="mt-3 w-100"
              disabled={!objective.trim()}
            >
              Continue
            </Button>
          </>
        )}

        {step === 1 && (
          <>
            <h4>List your options</h4>
            <Table borderless size="sm" className="mb-0">
              <thead><tr><th>Name</th><th>Description</th><th/></tr></thead>
              <tbody>
                {options.map((opt,i)=>(
                  <tr key={i}>
                    <td>
                      <Form.Control
                        value={opt.name}
                        onChange={e=>{
                          const o=[...options]; o[i].name=e.target.value; setOptions(o);
                        }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        value={opt.desc}
                        onChange={e=>{
                          const o=[...options]; o[i].desc=e.target.value; setOptions(o);
                        }}
                      />
                    </td>
                    <td>
                      <Button variant="outline-danger" size="sm" onClick={()=>removeRow(i)}>×</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Button variant="link" size="sm" className="mt-1" onClick={addRow}>
              + Add another
            </Button>

            <Form.Group className="mt-3 d-flex align-items-center">
              <Form.Label className="mb-0">
                Out of all your options, how many do you need to pick?
              </Form.Label>
              <Form.Select
                value={numToPick}
                onChange={e=>setNumToPick(Number(e.target.value))}
                className="ms-3"
                style={{ width:"60px", display:"inline-block" }}
              >
                {[...Array(7)].map((_,i)=>
                  <option key={i+1} value={i+1}>{i+1}</option>
                )}
              </Form.Select>
            </Form.Group>

            <Button
              className="mt-3 w-100"
              onClick={handleSubmit}
              disabled={options.some(o=>!o.name||!o.desc)}
            >
              Let’s Veto!
            </Button>
          </>
        )}

        {step === 2 && (
          questions.length > 0 ? (
            <>
              <h4>{questions[qIndex].question}</h4>
              <div className="d-flex gap-3 mt-3">
                <Button
                  variant="outline-primary"
                  className="flex-fill text-center"
                  onClick={()=>handleAnswer("if_1")}
                >
                  {questions[qIndex].if_1.answer_label}
                </Button>
                <Button
                  variant="outline-primary"
                  className="flex-fill text-center"
                  onClick={()=>handleAnswer("if_2")}
                >
                  {questions[qIndex].if_2.answer_label}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center text-muted">Loading questions…</div>
          )
        )}

        {step === 4 && (
          <>
            <h4>Final Results</h4>
            {(() => {
              const kept = Object.keys(result.final_reason_selected || {});
              const rej  = Object.keys(result.final_reason_rejected || {});
              return (
                <div>
                  <h5>Kept:</h5>
                  <ul>
                    {kept.map(opt=>(
                      <li key={opt}>
                        <strong>{opt}</strong>: {result.final_reason_selected[opt]}
                      </li>
                    ))}
                  </ul>
                  <h5>Rejected:</h5>
                  <ul>
                    {rej.map(opt=>(
                      <li key={opt}>
                        <strong>{opt}</strong>: {result.final_reason_rejected[opt]}
                      </li>
                    ))}
                  </ul>
                  {readyToContinue && (
                    <Button
                      variant="primary"
                      style={{ width: 260, height: 44, fontWeight: 600, fontSize: 17, marginTop: 28 }}
                      onClick={handleContinue}
                    >
                      Continue
                    </Button>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </Card>
    </>
  );
}
